/**
 * Handle all reducers (in redux way).
 *
 * Reducers specify how the application's state changes in response to actions
 * sent to the store. Actions only describe what happened, but don't describe
 * how the application's state changes.
 *
 * This module is internal Api. 
 * 
 * @module geo/reducers
 * 
 * @since v 2.0.1
 * @preferred
 * 
 * @private
 * @internalapi
 * 
 */

/**
 * @hidden
 */
import clone from 'lodash.clone';
import produce from "immer";
import { combineReducers, Reducer, ActionCreator } from 'redux';
import { reducerWithInitialState, reducerWithoutInitialState, } from 'typescript-fsa-reducers/dist';

import { ClearService } from '../actions/application/clear.service';
import { PendingService } from '../types/services/pending.service';
import { addMarkersAsyncCreator, zoomInAsyncCreator } from '../actions/creators';

import { callServiceMethodAsyncCreator, callServiceMethodCreator, callServiceRESTAsyncCreator, callServiceRESTCreator, serviceExecuteSearchAsyncCreator, serviceExecuteSearchCreator, serviceFeatureTransactionAsyncCreator, serviceFeatureTransactionCreator } from '../actions/creators/services.creator';
import { clearServiceCreator, pendingServiceCreator, ApplicationAvailableCreator } from '../actions/creators/application.creators';
import { BASELAYERS_UPDATEDCreator, COMPONENTS_STATE_UPDATEDCreator, EVENT_GEOCreator, INIT_APPLICATION_STATECreator, INITIALIZE_MAP_STATECreator, MAP_ENTRIES_UPDATEDCreator, MAP_EVENTCreator, SERVICE_DONECreator, SERVICE_EXCEPTIONCreator, UPDATE_EXTENTCreator, UPDATE_FILTER_HISTORY_TIMESTAMPCreator, UPDATE_FUNCTIONALITIES_STATECreator, UPDATE_FUNCTIONALITY_STATECreator, UPDATE_GLOBAL_PARAMETERCreator, UPDATE_MARKERSCreator, UPDATE_SELECTIONCreator } from '../actions/creators/proxy.creators';
import { RasterMapEntry } from '../map/layer/raster.map.entry';
import { SpatialDataMapEntry } from '../map/layer/spatial.data.map.entry';
import { WebServiceMapEntry } from '../map/layer/webService.map.entry';
import { BASELAYERS_UPDATED, MAP_EVENT, UPDATE_FILTER_HISTORY_TIMESTAMP, UPDATE_FUNCTIONALITIES_STATE, UPDATE_FUNCTIONALITY_STATE, UPDATE_GLOBAL_PARAMETER, UPDATE_MARKERS_LIST, UPDATE_SELECTION, MAP_ENTRIES_UPDATED } from '../payload/proxy';
import { COMPONENTS_STATE_UPDATED } from '../payload/proxy/COMPONENTS_STATE_UPDATED';
import { INITIALIZE_MAP_STATE } from '../payload/proxy/INITIALIZE_MAP_STATE';
import { SERVICE_DONE } from '../payload/proxy/SERVICE_DONE';
import { SERVICE_EXCEPTION } from '../payload/proxy/SERVICE_EXCEPTION';
import { IHashServicesOperations } from '../types/services';
import { ServiceOperationState } from '../types/operation.state';
import { UPDATE_EXTENT } from '../payload/proxy/UPDATE_EXTENT';
import { AppStore } from '../store/app';
import { BaseLayer } from '../types/base.layer';
import { IHashComponents } from '../types/component';
import { Feature } from '../types/feature';
import { FunctionalityOptions, IHashFunctionalities } from '../types/functionality';
import { GeoEvent } from '../types/geo.event';
import { GlobalParameter } from '../types/global.parameter';
import { History } from '../types/history';
import { MapOptions } from '../types/map';
import { GroupMapEntry } from '../types/map/layer/group.map.entry';
import { IHashEntry, MapEntry } from '../types/map/layer/map.entry';
import { VectorLayerMapEntry } from '../types/map/layer/vector.layer.map.entry';
import { IHashMarker, Marker } from '../types/map/marker';
import { Success } from '../actions/helpers/fsa';
import { ZOOM_IN } from '../payload/facade';
import { ExtentDone } from '../payload/response';
import { IHashServices } from '../services/service.loader';
import { SERVICE_EXECUTE_SEARCH } from '../payload/facade/services/SERVICE_EXECUTE_SEARCH';
import { KindService } from '../types/services/kind.service';
import { ServiceException } from '../types/services/service.exception';
import { findLastIndex } from 'lodash-es';



/**
 * @hidden
 * @private
 */
const reducerFsa = reducerWithoutInitialState<AppStore>().case(
  INIT_APPLICATION_STATECreator, (state, newApp: AppStore) => {
    const functionalities: IHashFunctionalities = {};

    const available = [
      'com.bg.functionality.search.Search',
      'com.bg.functionality.map.layer.Analysis',
      'com.bg.functionality.analysis2.Analysis2',
      'com.bg.functionality.infosheet.InfoSheet',
      'com.bg.functionality.stat.Statistic',
      'com.bg.functionality.geometrymodification.GeometryModification'
    ]
    newApp.functionalities = newApp.functionalities as IHashFunctionalities;
    for (const funcId in newApp.functionalities) {
      if (newApp.functionalities.hasOwnProperty(funcId)) {
        //@todo better builder setter
        const func: FunctionalityOptions = newApp.functionalities[funcId];
        if (func['@type'] != undefined) {
          const type: string = func['@type'];

          if (available.indexOf(type) !== -1 && undefined !== func.id) {
            const id: string = func.id;
            functionalities[id] = newApp.functionalities[funcId];
          }
        }
      }
    }

    const newState = {
      ...state,
      ...newApp,
      'applicationInitialized': true,
      'iframeInitialized': true,
      'functionalities': functionalities
    };
    return newState;
  });
/**
 * @hidden
 * @private
 */
function reduceMapEntries(entries: MapEntry[]) {
  const hashEntries: IHashEntry = {};
  for (const entry of entries) {
    let anEntry: MapEntry | undefined;
    let found: boolean = false;
    if (entry['@type'] === 'com.bg.functionality.map.layer.GroupMapEntry') {
      anEntry = entry as GroupMapEntry;
      found = true;
    }
    if (entry['@type'] ===
      'com.bg.functionality.map.layer.VectorLayerMapEntry') {
      anEntry = entry as VectorLayerMapEntry;
      found = true;
    }
    if (entry['@type'] ===
      'com.bg.functionality.map.layer.SpatialDataMapEntry') {
      anEntry = entry as SpatialDataMapEntry;
      found = true;
    }
    if (entry['@type'] ===
      'com.bg.functionality.map.layer.WebServiceMapEntry') {
      anEntry = entry as WebServiceMapEntry;
      found = true;
    }
    if (entry['@type'] === 'com.bg.functionality.map.layer.RasterMapEntry') {
      anEntry = entry as RasterMapEntry;
      found = true;
    }
    if (!found) {
      throw new Error('typeof entry not found');
    } else {
      if (anEntry !== undefined) {
        const key = anEntry.key;
        if (anEntry.entries !== undefined && Array.isArray(anEntry.entries)) {
          anEntry.entries = reduceMapEntries(anEntry.entries);
        }
        hashEntries[key] = anEntry;
      }
    }
  }
  return hashEntries;
}

/**
 * @hidden
 * @private
 */
const reducersMap =
  reducerWithInitialState<MapOptions | null | undefined>({})
    .case(
      INITIALIZE_MAP_STATECreator,
      (stateMap, map: INITIALIZE_MAP_STATE) => {
        if (Array.isArray(map.map.markers)) {
          map.map.markers = {};
        }

        // transform entries as hash
        let hashEntries: IHashEntry = {};

        if (Array.isArray(map.map.entries)) {
          hashEntries = reduceMapEntries(map.map.entries);
        }


        const mapReduced = { ...stateMap, ...map.map };
        mapReduced.entries = hashEntries;
        mapReduced.initialized = true;
        return mapReduced;
      })
    .case(
      MAP_ENTRIES_UPDATEDCreator,
      (stateMap: MapOptions | null | undefined, entriesUpdatedAction: MAP_ENTRIES_UPDATED) => {
        let mapReduced;
        if (stateMap !== undefined && stateMap !== null &&
          stateMap.entries !== undefined) {
          const newEntries = stateMap.entries;

          if (Array.isArray(entriesUpdatedAction.entries)) {
            const entriesUpdated = entriesUpdatedAction.entries;
            for (const idx in entriesUpdated) {
              if (entriesUpdated.hasOwnProperty(idx)) {
                const entryToUpdate = entriesUpdated[idx];
                const recurseFind =
                  (entries: IHashEntry): MapEntry | false => {
                    for (const jx in entries) {
                      if (entries.hasOwnProperty(jx)) {
                        const entryCompare = entries[jx];
                        if (entryCompare.key === entryToUpdate.key) {
                          return entries[jx];
                        } else {
                          if (entryCompare.entries !== undefined &&
                            entryCompare.entries) {
                            const result =
                              recurseFind(entryCompare.entries);
                            if (result) {
                              return result;
                            }
                          }
                        }
                      }
                    }

                    return false;
                  };
                const entryUpdated: any = recurseFind(newEntries);
                if (entryUpdated) {
                  Object.assign(entryUpdated, entryToUpdate);
                }
              }
            }
          }

          mapReduced = {
            ...stateMap,
            entries: Object.assign({}, newEntries)
          };
        } else {
          mapReduced = stateMap;
        }
        return mapReduced;
      })
    .case(
      UPDATE_EXTENTCreator,
      (stateMap, extentParams: UPDATE_EXTENT) => {
        return {
          ...stateMap,
          'extent': extentParams.extent,
          position: extentParams.center,
          scaleDenominator: extentParams.scaleDenominator
        };
      })
    .case(
      UPDATE_MARKERSCreator,
      (stateMap, markersParams: UPDATE_MARKERS_LIST) => {
        const markers: IHashMarker = {};
        if (markersParams.markers !== undefined) {
          let funcId;
          for (funcId in markersParams.markers) {
            if (markersParams.markers.hasOwnProperty(funcId)) {
              const func: Marker = markersParams!.markers[funcId];
              if (func !== undefined) {
                markers[func.id] = func;
              }
            }
          }
        }
        return { ...stateMap, 'markers': markers };
      })
    .case(
      addMarkersAsyncCreator.done,
      (stateMap, success: any) => {
        let markers: IHashMarker = {};
        const markersAdded = success.result;
        if (stateMap!.markers && stateMap!.markers !== undefined) {
          markers = stateMap!.markers as IHashMarker;
        }
        if (markersAdded.markers !== undefined) {
          let funcId;
          for (funcId in markersAdded.markers) {
            if (markersAdded.markers.hasOwnProperty(funcId)) {
              const func: Marker = markersAdded!.markers[funcId];
              if (func !== undefined) {
                markers[func.id] = func;
              }
            }
          }
        }
        return { ...stateMap, 'markers': markers };
      })
    .case(
      MAP_EVENTCreator,
      (stateMap, mapEvent: MAP_EVENT) => {
        switch (mapEvent.eventName) {
          case 'POINTER_MOVE':
            return { ...stateMap, 'pointerMove': mapEvent.args };
          case 'POINTER_CLICK':
            return { ...stateMap, 'pointerClick': mapEvent.args };

          default:
            return stateMap;
        }
      })
    .case(zoomInAsyncCreator.done, (stateMap, doneExtent: Success<ZOOM_IN, ExtentDone>) => {
      return ({ ...stateMap, 'extent': doneExtent.result.extent });
    });
/**
 * @hidden
 * @private
 */
const reducerEventBus =
  reducerWithInitialState<GeoEvent | null | undefined>(null).case(
    EVENT_GEOCreator, (lastEvent: any, newEvent: GeoEvent) => newEvent);
/**
 * @hidden
 * @private
 */

const reducerServiceDone =
  reducerWithInitialState<IHashServicesOperations>({})
    .casesWithAction(
      [
      serviceFeatureTransactionAsyncCreator.started,
      serviceExecuteSearchAsyncCreator.started,
      ],
      (stateServices, action) => {
        
        if (action.payload.transient) {
          const nextServices: IHashServicesOperations = produce<IHashServicesOperations>(stateServices, draftServices => {
            const id = action.payload.id;
            let newOperation = {
              id,
              state: ServiceOperationState.pending,
              type: action.payload.kindService,
              params: action.payload.parameters,
              transient: true,
            }
            newOperation = newOperation as PendingService;
            draftServices[id] = {
              id,
              state: ServiceOperationState.pending,
              transient: true,
              type: action.payload.kindService,
              operation: newOperation
            };


          })

          return nextServices;
        } else {
          return stateServices;
        }
      }

    )
    .casesWithAction(
      [
        serviceExecuteSearchAsyncCreator.done,
        serviceFeatureTransactionAsyncCreator.done
      ],
      (stateServices, action) => {
   
        if (action!.meta!.transient) {
          const nextServices: IHashServicesOperations = produce<IHashServicesOperations>(stateServices, draftServices => {
            const id = action.payload.params.id;
            const resultset = {
              id: action.payload.params.id,
              result: action.payload.result.result,
              params: action.payload.params,
              type: action.payload.params.kindService,
              transient: true,
            }
            draftServices[id] = {
              id,
              state: ServiceOperationState.done,
              transient: true,
              type: action.payload.params.kindService,
              operation: resultset
            };
          });
          return nextServices;
        }
        return stateServices;

      }

    )
    .casesWithAction(
      [
        serviceExecuteSearchAsyncCreator.failed,
        serviceFeatureTransactionAsyncCreator.failed
      ],
      (stateServices, action) => {
        alert('ob');
        if (action!.meta!.transient) {
          let nextServices: IHashServicesOperations = produce<IHashServicesOperations>(stateServices, draftServices => {
            const id = action.payload.params.id;
            const resultset = {
              id: action.payload.params.id,
              error: action.payload.error,
              type: action.payload.params.kindService,
              transient: true,
            }
            let resultsetTyped = resultset as ServiceException;
            draftServices[id] = {
              id: id,
              state: ServiceOperationState.exception,
              transient: true,
              type: action.payload.params.kindService,
              operation: resultsetTyped
            };
          });
          return nextServices;
        }

        return stateServices;

      }

    )
    .casesWithAction(
      [clearServiceCreator
      ],
      (stateServices, action) => {
        let id = action.payload.id;
        if (stateServices[id] != undefined) {
          let nextServices: IHashServicesOperations = produce<IHashServicesOperations>(stateServices, draftServices => {
            delete draftServices[id];
          });
          return nextServices;
        } else {
          return stateServices;
        }

      }
    )


/**
 * @hidden
 * @private
 */
const reducersFunctionalities = reducerWithInitialState<IHashFunctionalities>(
    {}
)
    .case(
        UPDATE_FUNCTIONALITIES_STATECreator,
        (functionalitiesOld, functionalities: UPDATE_FUNCTIONALITIES_STATE) => {
            const newFunctionalities: IHashFunctionalities = produce<IHashFunctionalities>(functionalitiesOld, draftFunctionalites => {
                for (const cnt in functionalities.configurations) {
                    if (functionalities.configurations.hasOwnProperty(cnt)) {
                        const func = functionalities.configurations[cnt];
                        const id = func!.functionality!.id;
                        if (id) {
                            draftFunctionalites[id].active = func.active;
                        }
                    }
                }
            });
            return newFunctionalities;
        }
    )
    .case(
        UPDATE_FUNCTIONALITY_STATECreator,
        (functionalitiesOld, functionalityNew: UPDATE_FUNCTIONALITY_STATE) => {
            const newFunctionalities: IHashFunctionalities = produce<IHashFunctionalities>(functionalitiesOld, draftFunctionalites => {
                const func = functionalityNew.configuration;
                const id = func!.functionality!.id;
                if (id) {
                    draftFunctionalites[id].active = func.active;
                    draftFunctionalites[id].visible = func.visible;
                    draftFunctionalites[id].results = functionalityNew.resultset;
                    draftFunctionalites[id].parameters = functionalityNew.parameters;
                }
            });
            return newFunctionalities;
        }
    );


const reducerInitialize = reducerWithInitialState<boolean | undefined>(false).case(
  ApplicationAvailableCreator,
  (laststate, newstate) => {
    return newstate.status;
  }
)
/**
 * @hidden
 * @private
 */
const reducersSelection = reducerWithInitialState<Feature[]>([]).case(
  UPDATE_SELECTIONCreator,
  (lastSelection, newSelection: UPDATE_SELECTION) => {
    return newSelection.selection;
  });
/**
 * @hidden
 * @private
 */
function iframeInitializedReducer(state = false, action: any) {
  return state;
}
/**
 * @hidden
 * @private
 */
function uidReducer(state: any = null, action: any) {
  return state;
}
/**
 * @hidden
 * @private
 */
function applicationInitializedReducer(state = false, action: any) {
  return state;
}
/**
 * @hidden
 * @private
 */
function configurationsReducer(state: any, action: any) {
  if (state === undefined) {
    return null;
  }
  return state;
}
/**
 * @hidden
 * @private
 */
function reducersId(state: any, action: any) {
  if (state === undefined) {
    return null;
  }
  return state;
}
/**
 * @hidden
 * @private
 */
function errorReducers(state: BaseLayer[] | null | undefined, action: any) {
  if (state === undefined) {
    return null;
  }
  return state;
}

/**
 * @hidden
 * @private
 */
const reducersGlobalParameters =
  reducerWithInitialState<GlobalParameter[] | null | undefined>([]).case(
    UPDATE_GLOBAL_PARAMETERCreator,
    (oldval, newval: UPDATE_GLOBAL_PARAMETER) => {
      if (oldval === undefined) {
        return null;
      }
      let mapReduced;
      if (oldval) {
        const newBl = oldval;
        if (newval) {
          const bls = newval.globalParameters;
          for (const blidx in bls) {
            if (bls.hasOwnProperty(blidx)) {
              const bl = bls[blidx];
              for (const idx in newBl) {
                if (newBl.hasOwnProperty(idx)) {
                  let appbl = newBl[idx];
                  if (appbl.id === bl.id) {
                    appbl = Object.assign(appbl, bl);
                  }
                }
              }
            }
          }
        }
        mapReduced = Object.assign({}, newBl);
      } else {
        mapReduced = oldval;
      }
      return mapReduced;
    });


/**
 * @hidden
 * @private
 */
const reducersDataHistory =
  reducerWithInitialState<History | null | undefined>(null).case(
    UPDATE_FILTER_HISTORY_TIMESTAMPCreator,
    (oldval, newval: UPDATE_FILTER_HISTORY_TIMESTAMP) => {
      if (newval === undefined || newval.timestamp === undefined) {
        return null;
      }
      const history = Object.assign({}, oldval);
      Object.assign(history, newval);
      return history;
    });
/**
 * @hidden
 * @private
 */
const reducersBaseLayer =
  reducerWithInitialState<BaseLayer[] | null | undefined>([]).case(
    BASELAYERS_UPDATEDCreator, (oldval, newval: BASELAYERS_UPDATED) => {
      if (oldval === undefined) {
        return null;
      }
      let mapReduced;
      if (oldval) {
        const newBl = oldval;
        if (newval) {
          const bls = newval.baseLayers;
          for (const blidx in bls) {
            if (bls.hasOwnProperty(blidx)) {
              const bl = bls[blidx];
              for (const idx in newBl) {
                if (newBl.hasOwnProperty(idx)) {
                  let appbl = newBl[idx];
                  if (appbl.id === bl.id) {
                    appbl = Object.assign(appbl, bl);
                  }
                }
              }
            }
          }
        }
        mapReduced = Object.assign({}, newBl);
      } else {
        mapReduced = oldval;
      }
      return mapReduced;
    });
/**
 * @hidden
 * @private
 */
function resourcesReducer(state: any, action: any) {
  if (state === undefined) {
    return null;
  }
  return state;
}


/**
 * @hidden
 * @private
 */
const reducersComponents =
  reducerWithInitialState<IHashComponents | null | undefined>({}).case(
    COMPONENTS_STATE_UPDATEDCreator,
    (oldval, newval: COMPONENTS_STATE_UPDATED) => {
      if (oldval === undefined) {
        return null;
      }
      let appReduced;
      if (oldval) {
        const newComp = oldval;
        if (newval) {
          const components = newval.components;
          for (const compidx in components) {
            if (components.hasOwnProperty(compidx)) {
              const comp = components[compidx];
              if (newComp.hasOwnProperty(compidx)) {
                let appComp = newComp[compidx];
                appComp = Object.assign(appComp, comp);
              }
            }
          }
        }
        appReduced = Object.assign({}, newComp);
      } else {
        appReduced = oldval;
      }
      return appReduced;
    });


/**
 * @hidden
 * @private
 */
const rootReducer = combineReducers<AppStore>({
  applicationInitialized: applicationInitializedReducer,
  baseLayers: reducersBaseLayer,
  initialized: reducerInitialize,
  components: reducersComponents,
  configurations: configurationsReducer,
  error: errorReducers,
  events: reducerEventBus,
  functionalities: reducersFunctionalities,
  globalParameters: reducersGlobalParameters,
  id: reducersId,
  iframeInitialized: iframeInitializedReducer,
  map: reducersMap,
  resources: resourcesReducer,
  selection: reducersSelection,
  servicesOperations: reducerServiceDone,
  uid: uidReducer,
  history: reducersDataHistory

});
/**
 * @hidden
 * @private
 */
const reducerApp: Reducer<AppStore, any> = (state: AppStore, action: any) => {
  let stateApplicaton = rootReducer(state, action);
  stateApplicaton = { ...state, ...stateApplicaton };
  if (action.type.substr(-6) === 'FAILED') {
    stateApplicaton.error = action.payload;
  }
  stateApplicaton = reducerFsa(stateApplicaton, action);
  return stateApplicaton;
};
/**
 * @hidden
 * @private
 */
const mainReducer = reducerApp;

/**
 * @hidden
 * @private
 */
export { mainReducer };
